import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { apiFetchInit, apiOrigin } from "@/lib/apiOrigin";
import { atwAuthSignoutUrl } from "@/lib/atwAuthHost";
import { atwDirectoryAppUrl } from "@/lib/atwDirectoryHost";

export type DirectorySessionUser = {
  id?: string;
  email?: string | null;
  name?: string | null;
  role?: string | null;
  orgId?: string | null;
};

type SessionState = {
  data: { user?: DirectorySessionUser } | null;
  status: "loading" | "authenticated" | "unauthenticated";
  refresh: () => void;
};

const SessionContext = createContext<SessionState>({
  data: null,
  status: "loading",
  refresh: () => {},
});

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<SessionState, "refresh">>({
    data: null,
    status: "loading",
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setState((s) => ({ ...s, status: "loading" }));
      try {
        const res = await fetch(`${apiOrigin()}/api/auth/session`, {
          credentials: "include",
          cache: "no-store",
          ...apiFetchInit(),
        });
        if (cancelled) return;
        if (!res.ok) {
          setState({ data: null, status: "unauthenticated" });
          return;
        }
        const data = (await res.json()) as { user?: DirectorySessionUser };
        if (data?.user?.email) {
          setState({ data: { user: data.user }, status: "authenticated" });
        } else {
          setState({ data: null, status: "unauthenticated" });
        }
      } catch {
        if (!cancelled) setState({ data: null, status: "unauthenticated" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refresh = () => setTick((t) => t + 1);

  return (
    <SessionContext.Provider value={{ ...state, refresh }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  return useContext(SessionContext);
}

export function signOut() {
  void (async () => {
    const origin = apiOrigin();
    try {
      const csrfRes = await fetch(`${origin}/api/auth/csrf`, {
        credentials: "include",
        cache: "no-store",
        ...apiFetchInit(),
      });
      if (csrfRes.ok) {
        const { csrfToken } = (await csrfRes.json()) as { csrfToken?: string };
        if (csrfToken) {
          await fetch(`${origin}/api/auth/signout`, {
            method: "POST",
            credentials: "include",
            cache: "no-store",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({
              csrfToken,
              callbackUrl: atwDirectoryAppUrl("/"),
            }),
            ...apiFetchInit(),
          });
        }
      }
    } catch {
      /* Auth hub sign-out still runs below */
    }
    window.location.assign(atwAuthSignoutUrl(atwDirectoryAppUrl("/")));
  })();
}
